import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";

export function AuthCard({ eyebrow, title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 hero-gradient">
      <div className="absolute top-8 left-8 flex items-center gap-3">
        <img src="/shuttle-logo.png" alt="" className="h-9 w-9 object-contain" />
        <span className="font-display text-lg font-semibold tracking-tight">Mission Control</span>
      </div>
      <div className="w-full max-w-sm bg-white border border-[#EEEFEF] rounded-3xl premium-shadow p-8 animate-fade-up">
        {eyebrow && <p className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#723FFF] mb-2.5">{eyebrow}</p>}
        <h1 className="font-display text-2xl font-semibold tracking-tight text-[#06060C]">{title}</h1>
        {subtitle && <p className="mt-2 text-sm text-[#9FA1A4] leading-relaxed">{subtitle}</p>}
        <div className="mt-7">{children}</div>
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (!token) return <AuthCard title="Invalid link" subtitle="This reset link is missing its token. Please request a new one from the sign-in screen." />;

  if (done) return (
    <AuthCard eyebrow="All set" title="Password updated">
      <div className="flex items-center gap-2 text-[#1f8f5a] mb-6"><CheckCircle2 className="h-5 w-5" /><span className="text-sm">Your password has been reset.</span></div>
      <Button data-testid="reset-goto-login" onClick={() => navigate("/console")} className="w-full h-12 bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">Go to sign in <ArrowRight className="ml-1 h-4 w-4" /></Button>
    </AuthCard>
  );

  return (
    <AuthCard eyebrow="Reset password" title="Choose a new password" subtitle="Enter a new password for your Mission Control account.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-[11px] text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">New password</label>
          <Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className="bg-[#F6F6FD] border-[#EEEFEF] h-12 rounded-2xl px-4" required />
        </div>
        <div>
          <label className="text-[11px] text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">Confirm password</label>
          <Input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" className="bg-[#F6F6FD] border-[#EEEFEF] h-12 rounded-2xl px-4" required />
        </div>
        {error && <p data-testid="reset-error" className="text-sm text-[#d83a3f]">{error}</p>}
        <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full h-12 bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
        </Button>
      </form>
    </AuthCard>
  );
}
